import EventHandlerResult = OfficeExtension.EventHandlerResult;
import { ExcelValidationError } from "./ExcelValidationError";

export const getUsedRangeWithProperties = async (
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  properties: string[]
) => {
  const range = sheet.getUsedRange();
  range.load(properties);
  await context.sync();
  return range;
};

/**
 *
 * @param range columnCount already loaded
 * @param context
 * @param worksheetName
 */
export const getHeaderToIndexMap = async (
  range: Excel.Range,
  context: Excel.RequestContext,
  worksheetName?: string
) => {
  const headers = range.getRow(0);
  headers.load("values");
  await context.sync();

  const headerToIndex = new Map<string, number>();
  for (let i = 0; i < range.columnCount; i++) {
    const header = headers.values[0][i];
    if (!header) {
      continue;
    }
    if (headerToIndex.has(header)) {
      throw new ExcelValidationError(`Duplicate column ${header}`, worksheetName);
    }
    headerToIndex.set(header, i);
  }
  return headerToIndex;
};

/**
 *
 * @param range rowCount already loaded
 * @param context
 * @param properties to load
 */
export const getRowsWithProperties = async (
  range: Excel.Range,
  context: Excel.RequestContext,
  properties: string[]
) => {
  const rows: Excel.Range[] = [];
  for (let i = 0; i < range.rowCount; i++) {
    const row = range.getRow(i);
    row.load(properties);
    rows.push(row);
  }
  await context.sync();
  return rows;
};

export const registerWorksheetNamesOnChangeHandler = async (onChange: (worksheets: string[]) => void) => {
  return Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    return [
      worksheets.onAdded.add(worksheetNamesOnChange(onChange)),
      worksheets.onDeleted.add(worksheetNamesOnChange(onChange)),
      // Not working on Windows Excel as of March 2024
      // worksheets.onNameChanged.add(worksheetNamesOnChange(onChange)),
    ];
  });
};

const worksheetNamesOnChange = (onChange: (worksheets: string[]) => void) => {
  return async () => onChange(await getWorksheetNames());
};

export const getWorksheetNames = async () => {
  return Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items");
    await context.sync();
    return worksheets.items.map((item) => item.name);
  });
};

export const registerWorksheetOnChangeHandler = async (onChange: () => Promise<void>, worksheetName: string) => {
  return Excel.run(async (context) => {
    if (!worksheetName) return null;

    const worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
    await context.sync();

    if (!worksheet.isNullObject) {
      return worksheet.onChanged.add(onChange);
    }
    return null;
  });
};

export const removeEventHandler = async (eventHandler: EventHandlerResult<any>) => {
  await Excel.run(eventHandler.context, async (context) => {
    eventHandler.remove();
    await context.sync();
  });
};

/**
 *
 * @param range columnCount already loaded
 * @param horizontalAlignment
 */
export const fitAndAlignColumns = (range: Excel.Range, horizontalAlignment: Excel.HorizontalAlignment) => {
  for (let column = 0; column < range.columnCount; column++) {
    range.getColumn(column).format.autofitColumns();
    range.getColumn(column).getEntireColumn().format.horizontalAlignment = horizontalAlignment;
  }
};

const DAY_MILLISECONDS = 86400000;
const MS_DAY_OFFSET = 25569;

// https://learn.microsoft.com/en-us/office/dev/add-ins/excel/excel-add-ins-ranges-dates
// https://github.com/markitondemand/moment-msdate/blob/master/moment-msdate.js
export const fromOADate = (oaDate: number) => {
  return new Date((oaDate - MS_DAY_OFFSET) * DAY_MILLISECONDS);
};
